import { useMemo, useState } from "react";
import { ScheduleLifecycleProposal } from "@common/schemas/domains/accounting/scheduleLifecycle.schema";
import { ScheduleChangeJournalEntryMode } from "@common/types/domains/accounting/schedule";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import {
    ScheduleOptionCard,
    ScheduleOptionCards,
} from "../options/ScheduleOptionCards";
import { ScheduleOptionsModal } from "../options/ScheduleOptionsModal";
import { SCHEDULE_LOCKED_PERIODS_GO_TO_LOCKS_LABEL } from "../scheduleLockedPeriodsCopy";
import { buildScheduleChangeOptionsCopy } from "./scheduleChangeOptionsCopy";
import { buildScheduleLifecycleCopy } from "./scheduleLifecycleCopy";

interface ScheduleChangeModalProps {
    show: boolean;
    proposal: ScheduleLifecycleProposal | undefined;
    isSubmitting: boolean;
    /** No mode when nothing is earned: there is nothing to correct. */
    onConfirm: (
        journalEntryMode: ScheduleChangeJournalEntryMode | undefined,
    ) => void;
    /** The unlock hint's link; the caller decides how to come back. */
    onGoToLocks: () => void;
    onHide: () => void;
    onExited: () => void;
}

/**
 * Change and how the earned entries are corrected are one decision. The
 * option the server recommends is preselected: reverse on open books, the
 * catch-up once an earned month is locked.
 */
export const ScheduleChangeModal = ({
    show,
    proposal,
    isSubmitting,
    onConfirm,
    onGoToLocks,
    onHide,
    onExited,
}: ScheduleChangeModalProps) => {
    const [chosenMode, setChosenMode] =
        useState<ScheduleChangeJournalEntryMode | null>(null);
    const changeOptions = proposal?.summary.changeOptions ?? null;
    const selectedMode =
        chosenMode ?? changeOptions?.defaultJournalEntryMode ?? null;

    const options = useMemo(
        (): Array<ScheduleOptionCard<ScheduleChangeJournalEntryMode>> =>
            buildScheduleChangeOptionsCopy(changeOptions).map((option) => ({
                value: option.mode,
                title: option.title,
                description: option.description,
                isDisabled: option.isDisabled,
                hint: option.lockedHint ? (
                    <HStack gap={4} align="center" wrap>
                        <span>{option.lockedHint}</span>
                        <Button
                            type="button"
                            variant="link"
                            size="xs"
                            onClick={onGoToLocks}
                        >
                            {SCHEDULE_LOCKED_PERIODS_GO_TO_LOCKS_LABEL}
                        </Button>
                    </HStack>
                ) : undefined,
            })),
        [changeOptions, onGoToLocks],
    );
    const hasOptions = options.length > 0;
    const copy = proposal ? buildScheduleLifecycleCopy(proposal) : null;

    return (
        <ScheduleOptionsModal
            show={show}
            content={
                copy
                    ? {
                          title: copy.title,
                          policyLine: copy.policyLine,
                          body: copy.body,
                          options: hasOptions ? (
                              <ScheduleOptionCards
                                  ariaLabel="Earned entries"
                                  options={options}
                                  value={selectedMode}
                                  onChange={setChosenMode}
                              />
                          ) : undefined,
                          confirmLabel: copy.confirmLabel,
                          isConfirmDisabled:
                              hasOptions && selectedMode === null,
                      }
                    : null
            }
            isSubmitting={isSubmitting}
            onConfirm={() =>
                onConfirm(hasOptions ? (selectedMode ?? undefined) : undefined)
            }
            onHide={onHide}
            onExited={() => {
                setChosenMode(null);
                onExited();
            }}
        />
    );
};
